/**
 * QuestionInput Component
 * 
 * Provides an input field and submit button for users to ask AWS questions.
 * Includes validation and loading state handling.
 * 
 * Requirements: 1.1, 1.4
 */

import React, { useState, FormEvent, ChangeEvent } from 'react';
import { validateQuestion } from '../utils/validation';

interface QuestionInputProps {
  onSubmit: (question: string) => void;
  isLoading: boolean;
}

export const QuestionInput: React.FC<QuestionInputProps> = ({ onSubmit, isLoading }) => {
  const [question, setQuestion] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate question before submission
    if (!validateQuestion(question)) {
      setValidationError('Please enter a question.');
      return;
    }

    // Submit the question
    onSubmit(question);
    
    // Clear the input after successful submission
    setQuestion('');
  };

  return (
    <form onSubmit={handleSubmit} className="question-input-form">
      <div className="input-container">
        <textarea
          value={question}
          onChange={handleInputChange}
          placeholder="Ask a question about AI and machine learning..."
          disabled={isLoading}
          className={`question-textarea ${validationError ? 'error' : ''}`}
          rows={4}
          aria-label="Question input"
          aria-invalid={!!validationError}
          aria-describedby={validationError ? 'validation-error' : undefined}
        />
        {validationError && (
          <div id="validation-error" className="validation-error" role="alert">
            {validationError}
          </div>
        )}
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="submit-button"
        aria-label="Submit question"
      >
        {isLoading ? 'Submitting...' : 'Ask Question'}
      </button>
    </form>
  );
};
